import React, { useState, useEffect } from 'react';
import { Button, Switch, FormControlLabel, Typography, Box, Alert } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import TestIcon from '@mui/icons-material/BugReport';
import { useAuth } from '../contexts/AuthContext';
import { 
    subscribeToPushNotification, 
    unsubscribeFromPushNotification, 
    checkPushSubscription,
    sendTestPushNotification
} from '../services/api';

const PushNotificationSettings: React.FC = () => {
    const { token } = useAuth();
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [debugInfo, setDebugInfo] = useState<any>(null);
    const [isTestLoading, setIsTestLoading] = useState(false);
    const [testSuccess, setTestSuccess] = useState<string | null>(null);
    const [testError, setTestError] = useState<string | null>(null);

    // デバッグ情報を収集
    const collectDebugInfo = React.useCallback(() => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isIOSChrome = isIOS && navigator.userAgent.includes('CriOS');
        const isIOSSafari = isIOS && !navigator.userAgent.includes('CriOS');
        
        const info = {
            userAgent: navigator.userAgent,
            hasServiceWorker: 'serviceWorker' in navigator,
            hasPushManager: 'PushManager' in window,
            hasNotification: 'Notification' in window,
            isIOS: isIOS,
            isIOSChrome: isIOSChrome,
            isIOSSafari: isIOSSafari,
            isStandalone: window.matchMedia('(display-mode: standalone)').matches,
            isFullscreen: window.matchMedia('(display-mode: fullscreen)').matches,
            isMinimalUI: window.matchMedia('(display-mode: minimal-ui)').matches,
            isBrowser: window.matchMedia('(display-mode: browser)').matches,
            notificationPermission: 'Notification' in window ? Notification.permission : 'not available',
            platform: navigator.platform,
            cookieEnabled: navigator.cookieEnabled,
            onLine: navigator.onLine,
            windowLocationHref: window.location.href,
            windowLocationOrigin: window.location.origin,
            isHttps: window.location.protocol === 'https:',
            serviceWorkerController: navigator.serviceWorker?.controller ? 'active' : 'none',
            // iOS特有の情報
            iosDetails: isIOS ? {
                detectedBrowser: isIOSChrome ? 'Chrome' : 'Safari',
                standaloneMode: window.matchMedia('(display-mode: standalone)').matches,
                canInstallPWA: !window.matchMedia('(display-mode: standalone)').matches
            } : null
        };
        setDebugInfo(info);
        return info;
    }, []);

    // 購読状態を確認
    useEffect(() => {
        const initializePushNotifications = async () => {
            try {
                // デバッグ情報を収集
                collectDebugInfo();
                
                // サーバー側の購読状態とブラウザ側の購読状態を同期して確認
                const subscribed = await checkPushSubscription(token || undefined);
                setIsSubscribed(subscribed);
                console.log('初期購読状態:', subscribed);
            } catch (err) {
                console.error('Push通知の初期化に失敗:', err);
                setIsSubscribed(false);
            }
        };
        
        // tokenが存在する場合のみ初期化
        if (token) {
            initializePushNotifications();
        } else {
            // トークンがない場合でもデバッグ情報は収集
            collectDebugInfo();
        }
    }, [token, collectDebugInfo]);

    // Push通知の有効化/無効化
    const handleToggleNotifications = async (enabled: boolean) => {
        console.log('Token:', token); // デバッグ用
        console.log('Enabled:', enabled); // デバッグ用
        
        if (!token) {
            setError('ログインが必要です');
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccess(null);

        try {
            if (enabled) {
                // 基本的なAPIの存在チェック
                if (!('serviceWorker' in navigator)) {
                    setError('お使いのブラウザはService Workerに対応していません');
                    return;
                }
                
                if (!('Notification' in window)) {
                    setError('お使いのブラウザは通知APIに対応していません');
                    return;
                }
                
                // iOSでない場合のみPushManagerをチェック
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                if (!isIOS && !('PushManager' in window)) {
                    setError('お使いのブラウザはPush APIに対応していません');
                    return;
                }
                
                // まず通知許可を確認
                let permission = Notification.permission;
                
                if (permission === 'default') {
                    console.log('通知許可をリクエストします...');
                    permission = await Notification.requestPermission();
                    console.log('通知許可の結果:', permission);
                    
                    // iOSの場合、許可ダイアログの応答後に少し待機
                    if (isIOS) {
                        console.log('iOS: 許可ダイアログ応答後の待機...');
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        // 再度許可状態を確認
                        permission = Notification.permission;
                        console.log('iOS: 再確認後の許可状態:', permission);
                    }
                }
                
                if (permission === 'denied') {
                    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
                    
                    if (isIOS && !isStandalone) {
                        setError('iOSでは、サイトをホーム画面に追加してからPush通知を有効にしてください。その後、iOSの設定 → 通知 → 追加したアプリ → 通知を許可してください。');
                    } else {
                        setError('ブラウザでPush通知が拒否されています。ブラウザの設定で通知を許可してください。');
                    }
                    return;
                }
                
                if (permission === 'granted') {
                    console.log('通知許可が得られました。Push通知を登録します...');
                    
                    try {
                        await subscribeToPushNotification(token);
                        setIsSubscribed(true);
                        setSuccess('Push通知が有効になりました');
                        console.log('Push通知の登録が完了しました');
                    } catch (subscribeError) {
                        console.error('Push通知の登録でエラー:', subscribeError);
                        // iOS PWAで初回登録時によくある一時的なエラーをハンドリング
                        if (isIOS && subscribeError instanceof Error && 
                            (subscribeError.message.includes('Registration failed') || 
                             subscribeError.message.includes('AbortError') ||
                             subscribeError.message.includes('not supported') ||
                             subscribeError.message.includes('permission denied'))) {
                            console.log('iOS: 初回登録エラーを検出、再試行を促します');
                            setError('通知許可の設定を確認しています。もう一度Push通知をONにしてください。');
                        } else {
                            throw subscribeError; // 他のエラーは通常通り処理
                        }
                    }
                } else if (permission === 'default') {
                    // defaultの場合は許可を求める処理が失敗している
                    if (isIOS) {
                        setError('通知許可の取得に失敗しました。iOSの設定 → 通知 → 追加したアプリで通知を許可してください。');
                    } else {
                        setError('通知許可の取得に失敗しました。ブラウザの設定で通知を許可してください。');
                    }
                } else {
                    setError(`通知許可の状態が不明です: ${permission}`);
                }
            } else {
                await unsubscribeFromPushNotification(token);
                setIsSubscribed(false);
                setSuccess('Push通知が無効になりました');
            }
        } catch (err) {
            console.error('Push通知の設定に失敗:', err);
            // 404エラーの場合は、既に削除されているものとして成功扱いにする
            if (err instanceof Error && err.message.includes('404')) {
                if (!enabled) {
                    setIsSubscribed(false);
                    setSuccess('Push通知が無効になりました');
                } else {
                    setError('Push通知の設定に失敗しました');
                }
            } else {
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                if (isIOS && err instanceof Error) {
                    // iOS特有のエラーメッセージを改善
                    if (err.message.includes('permission denied') || err.message.includes('denied')) {
                        setError('通知許可が必要です。iOSの設定 → 通知 → 追加したアプリで通知を許可してから、再度お試しください。');
                    } else if (err.message.includes('not supported') || err.message.includes('AbortError')) {
                        setError('iOSでPush通知を利用するには、サイトをホーム画面に追加し、iOSの設定で通知を許可してください。');
                    } else {
                        setError(`iOSでのPush通知設定でエラーが発生しました。もう一度お試しください。`);
                    }
                } else {
                    setError(`Push通知の設定に失敗しました: ${err instanceof Error ? err.message : 'Unknown error'}`);
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    // ブラウザがPush通知に対応しているかチェック
    const isSupported = React.useMemo(() => {
        // Service Worker とPushManagerの基本的な対応チェック
        const hasServiceWorker = 'serviceWorker' in navigator;
        const hasPushManager = 'PushManager' in window;
        const hasNotification = 'Notification' in window;
        
        // より厳密なiOS検出
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
                     navigator.userAgent.includes('iPhone') ||
                     navigator.userAgent.includes('iPad') ||
                     navigator.userAgent.includes('iPod');
        
        // iOS Chrome の場合の特別検出
        const isIOSChrome = isIOS && (navigator.userAgent.includes('CriOS') || navigator.userAgent.includes('Chrome'));
        
        console.log('Push API Support Check:', {
            hasServiceWorker,
            hasPushManager,
            hasNotification,
            isIOS,
            isIOSChrome,
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            maxTouchPoints: navigator.maxTouchPoints
        });
        
        // iOSの場合、基本的なAPIが存在すれば対応していると判定
        // PushManagerがなくてもService WorkerとNotificationがあれば十分
        if (isIOS) {
            const result = hasServiceWorker && hasNotification;
            console.log('iOS support result:', result);
            return result;
        }
        
        // iOS以外は従来通り
        const result = hasServiceWorker && hasPushManager && hasNotification;
        console.log('Non-iOS support result:', result);
        return result;
    }, []);

    // iOSの特別な案内が必要かどうか
    const iosStatus = React.useMemo(() => {
        // より厳密なiOS検出
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
                     navigator.userAgent.includes('iPhone') ||
                     navigator.userAgent.includes('iPad') ||
                     navigator.userAgent.includes('iPod');
        
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                           (window.navigator as any)?.standalone ||
                           window.location.search.includes('homescreen=1');
        
        // iOS Chrome の検出を改善
        const isIOSChrome = isIOS && (navigator.userAgent.includes('CriOS') || 
                                    (navigator.userAgent.includes('Chrome') && isIOS));
        
        const isIOSSafari = isIOS && !isIOSChrome;
        
        console.log('iOS Status Check:', {
            isIOS,
            isStandalone,
            isIOSChrome,
            isIOSSafari,
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            maxTouchPoints: navigator.maxTouchPoints
        });
        
        if (!isIOS) {
            return { isIOS: false, needsInstall: false, isInstalled: false, browser: 'other' };
        }
        
        return {
            isIOS: true,
            needsInstall: !isStandalone,
            isInstalled: isStandalone,
            browser: isIOSChrome ? 'chrome' : 'safari'
        };
    }, []);

    // iOSで未インストールの場合はPush通知を無効化
    const shouldDisablePushNotifications = iosStatus.isIOS && iosStatus.needsInstall;

    // テスト通知の送信
    const handleTestNotification = async () => {
        if (!token) {
            setTestError('ログインが必要です');
            return;
        }

        if (!isSubscribed) {
            setTestError('まずPush通知を有効にしてください');
            return;
        }

        setIsTestLoading(true);
        setTestError(null);
        setTestSuccess(null);

        try {
            const response = await sendTestPushNotification(token);
            if (response.status === 'success') {
                setTestSuccess('テスト通知を送信しました！通知が届いているかご確認ください。');
            } else {
                setTestError(response.message || 'テスト通知の送信に失敗しました');
            }
        } catch (error: any) {
            console.error('テスト通知の送信に失敗:', error);
            if (error.response && error.response.status === 401) {
                setTestError('認証エラーです。再度ログインしてください');
            } else {
                setTestError('テスト通知の送信に失敗しました');
            }
        } finally {
            setIsTestLoading(false);
            // 5秒後にメッセージをクリア
            setTimeout(() => {
                setTestSuccess(null);
                setTestError(null);
            }, 5000);
        }
    };

    // 基本的なAPIに対応していない場合のみ非対応メッセージを表示
    // ただし、iOSの場合は別途専用の案内を表示するので、ここでは除外
    if (!isSupported && !iosStatus.isIOS) {
        return (
            <Box 
                sx={{ 
                    mt: 2, 
                    p: 2, 
                    border: '2px solid #f44336',
                    borderRadius: 2,
                    backgroundColor: '#ffebee',
                    boxShadow: '0 2px 8px rgba(244, 67, 54, 0.10)'
                }}
            >
                <Typography 
                    variant="h6" 
                    sx={{ 
                        mb: 2, 
                        color: '#c62828',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <NotificationsOffIcon />
                    お使いのブラウザはPush通知に対応していません
                </Typography>

                <Alert severity="error" sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                            🚫 現在のブラウザではPush通知機能を利用できません
                        </Typography>
                        
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                            【推奨ブラウザ】
                        </Typography>
                        
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', mb: 1 }}>
                            <strong>スマートフォンの場合：</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', ml: 1, mb: 1 }}>
                            • <strong>iPhone/iPad:</strong> Safari または Chrome
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', ml: 1, mb: 1 }}>
                            • <strong>Android:</strong> Chrome、Firefox、Edge
                        </Typography>
                        
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', mb: 1 }}>
                            <strong>パソコンの場合：</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', ml: 1, mb: 1 }}>
                            • <strong>Windows/Mac:</strong> Chrome、Firefox、Edge、Safari（Mac）
                        </Typography>
                        
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666', mt: 2 }}>
                            上記のブラウザをご利用いただくことで、Push通知機能をお使いいただけます。
                        </Typography>
                    </Box>
                </Alert>

                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                    推奨ブラウザに変更後、再度このページにアクセスしてPush通知を設定してください
                </Typography>
            </Box>
        );
    }

    // iOSで未インストールの場合は専用の案内を表示
    if (shouldDisablePushNotifications) {
        return (
            <Box 
                sx={{ 
                    mt: 2, 
                    p: 2, 
                    border: '2px solid #ff9800',
                    borderRadius: 2,
                    backgroundColor: '#fff3e0',
                    boxShadow: '0 2px 8px rgba(255, 152, 0, 0.10)'
                }}
            >
                <Typography 
                    variant="h6" 
                    sx={{ 
                        mb: 2, 
                        color: '#e65100',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <NotificationsOffIcon />
                    Push通知を利用するには設定が必要です
                </Typography>

                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                            📱 iOSでPush通知を受け取るには、このサイトをホーム画面に追加して、アプリとして起動してください。
                        </Typography>
                        
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                            【現在の状況】
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', ml: 1, mb: 2 }}>
                            {iosStatus.browser === 'chrome' ? 'Chrome' : 'Safari'}アプリから通常のウェブサイトとしてアクセスしています
                        </Typography>
                        
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                            【設定手順】
                        </Typography>
                        
                        <Typography variant="body2" sx={{ fontSize: '0.9rem', mb: 1 }}>
                            <strong>設定手順：</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', ml: 1, mb: 1 }}>
                            1. 画面下部の共有ボタン（□↑）をタップ
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', ml: 1, mb: 1 }}>
                            2. 「ホーム画面に追加」をタップ
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', ml: 1, mb: 2 }}>
                            3. 「追加」をタップ
                        </Typography>
                        
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666', mb: 1 }}>
                            <strong>重要：</strong> ホーム画面に追加後は、必ずホーム画面のアイコンからアプリとして起動してください。
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
                            ブラウザから直接アクセスした場合、Push通知は利用できません。
                        </Typography>
                    </Box>
                </Alert>

                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                    設定完了後、ホーム画面のアイコンからアクセスしてPush通知を有効にできます
                </Typography>

            </Box>
        );
    }

    return (
        <Box 
            sx={{ 
                mt: 2, 
                p: 2, 
                border: '2px solid #f8bbd0',
                borderRadius: 2,
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(240, 98, 146, 0.10)'
            }}
        >
            {/* iOSでPWAとして正常にインストール済みの場合は成功メッセージを表示 */}
            {iosStatus.isIOS && iosStatus.isInstalled && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            ✅ ホーム画面への追加が完了しています！Push通知を利用できます
                        </Typography>
                    </Box>
                </Alert>
            )}
            <Typography 
                variant="h6" 
                sx={{ 
                    mb: 2, 
                    color: '#ad1457',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}
            >
                {isSubscribed ? <NotificationsIcon /> : <NotificationsOffIcon />}
                Push通知設定
            </Typography>

            <FormControlLabel
                control={
                    <Switch
                        checked={isSubscribed}
                        onChange={(e) => handleToggleNotifications(e.target.checked)}
                        disabled={isLoading}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#f06292',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#f06292',
                            },
                        }}
                    />
                }
                label={
                    <Typography sx={{ color: '#ad1457' }}>
                        {isSubscribed ? 'Push通知 有効' : 'Push通知 無効'}
                    </Typography>
                }
            />

            <Typography variant="body2" sx={{ mt: 1, color: '#666', fontSize: '0.875rem' }}>
                新しいヒントが投稿されたときに通知を受け取れます
            </Typography>

            {/* テスト通知ボタン - 一時的に非表示 */}
            {/* {isSubscribed && (
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<TestIcon />}
                        onClick={handleTestNotification}
                        disabled={isTestLoading || isLoading}
                        sx={{
                            borderColor: '#f06292',
                            color: '#f06292',
                            '&:hover': {
                                borderColor: '#e91e63',
                                backgroundColor: '#fce4ec',
                            },
                            fontSize: '0.875rem',
                            fontWeight: 600,
                        }}
                    >
                        {isTestLoading ? 'テスト送信中...' : 'テスト通知を送信'}
                    </Button>
                    <Typography variant="body2" sx={{ mt: 1, color: '#666', fontSize: '0.8rem' }}>
                        あなただけにテスト通知を送信します（他の人には届きません）
                    </Typography>
                </Box>
            )} */}

            {/* テスト通知の結果メッセージ - 一時的に非表示 */}
            {/* {testSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                    {testSuccess}
                </Alert>
            )}

            {testError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {testError}
                </Alert>
            )} */}

            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                    {success}
                </Alert>
            )}

            {isLoading && (
                <Typography variant="body2" sx={{ mt: 2, color: '#ad1457' }}>
                    設定を更新しています...
                </Typography>
            )}
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#ad1457', mb: 1 }}>
                    📱 Push通知を受け取るための条件
                </Typography>
                
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', mb: 1 }}>
                    <strong>iPhone/iPad（iOS）の場合：</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', ml: 1, mb: 1 }}>
                    • このサイトをホーム画面に追加してアプリとして使用してください
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', ml: 1, mb: 1 }}>
                    • Safari・Chrome: 共有ボタン（□↑）→ 「ホーム画面に追加」
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', ml: 1, mb: 1 }}>
                    • iOS設定 → 通知 → 追加したアプリ → 通知を許可
                </Typography>
                
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', mb: 1 }}>
                    <strong>Android スマートフォンの場合：</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', ml: 1, mb: 1 }}>
                    • ブラウザアプリをバックグラウンドで実行してください
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', ml: 1, mb: 1 }}>
                    • 端末の通知設定でブラウザからの通知を許可してください
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', ml: 1, mb: 1 }}>
                    • 省電力モードやバッテリー最適化でブラウザを制限しないでください
                </Typography>
                
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', mb: 1 }}>
                    <strong>PC（パソコン）の場合：</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', ml: 1, mb: 1 }}>
                    • ブラウザを完全に閉じないでください（最小化は可能）
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', ml: 1, mb: 1 }}>
                    • バックグラウンドでの通知を許可してください
                </Typography>
                
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', mb: 1 }}>
                    <strong>共通の注意事項：</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', ml: 1, mb: 1 }}>
                    • 初回設定時にブラウザの通知許可ダイアログで「許可」を選択してください
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', ml: 1 }}>
                    • 通知が届かない場合は、ブラウザの設定で通知が有効になっているか確認してください
                </Typography>
            </Box>

        </Box>
    );
};

export default PushNotificationSettings;
